import { z } from 'zod'
import { IsoDateTime } from './common'

// The Code node's batch: a lender's API code as versions (G3–G7, sheets F2–F3), now a kit feature.

export const CodeFile = z.object({
  path: z.string().describe('e.g. request.js, parse.js, README.md'),
  content: z.string(),
})
export type CodeFile = z.infer<typeof CodeFile>

export const CodeTest = z.object({
  id: z.string(),
  name: z.string(),
  reply: z.record(z.string(), z.unknown()).describe('a sample reply from the lender'),
  expectedOutcome: z.string(),
})
export type CodeTest = z.infer<typeof CodeTest>

export const CodeBatchVersionStatus = z.enum(['draft', 'built', 'live', 'retired'])

export const CodeBatchVersion = z.object({
  id: z.string(),
  batchId: z.string(),
  number: z.string().describe('e.g. 1.0.0'),
  status: CodeBatchVersionStatus,
  note: z.string().default(''),
  files: z.array(CodeFile),
  tests: z.array(CodeTest),
  build: z
    .object({ tag: z.string(), checksum: z.string(), builtAt: IsoDateTime, builtBy: z.string() })
    .nullable(),
  createdAt: IsoDateTime,
  createdBy: z.string(),
  madeLiveAt: IsoDateTime.nullable().default(null),
})
export type CodeBatchVersion = z.infer<typeof CodeBatchVersion>

export const CodeBatch = z.object({
  id: z.string(),
  workspaceId: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().default(''),
  liveVersionId: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
})
export type CodeBatch = z.infer<typeof CodeBatch>

export const CodeBatchInput = CodeBatch.pick({ slug: true, name: true, description: true })
export type CodeBatchInput = z.infer<typeof CodeBatchInput>

export const CodeVersionPatch = z.object({
  files: z.array(CodeFile).optional(),
  tests: z.array(CodeTest).optional(),
  note: z.string().optional(),
})
export type CodeVersionPatch = z.infer<typeof CodeVersionPatch>

export const CodeTestResult = z.object({
  testId: z.string(),
  name: z.string(),
  expectedOutcome: z.string(),
  actualOutcome: z.string().nullable(),
  ok: z.boolean(),
  log: z.array(z.string()),
  error: z.string().nullable(),
})
export type CodeTestResult = z.infer<typeof CodeTestResult>
